import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MagnifyingGlass, ChartLine, Target } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const SeoPortfolio = () => {
  const { t } = useTranslation();

  const portfolioItems = [
    {
      key: 'seo1',
      title: 'E-commerce SEO Boost',
      description: 'Complete SEO optimalisatie voor een webshop die resulteerde in 200% meer organisch verkeer en 150% meer verkopen.',
      category: 'E-commerce SEO',
      technologies: ['Keyword Research', 'Technical SEO', 'Content Strategy', 'Link Building'],
      features: ['200% Traffic Groei', '150% Verkoop Boost', 'Top 3 Rankings', 'Local SEO'],
      link: '#'
    },
    {
      key: 'seo2',
      title: 'Local Business Visibility',
      description: 'Lokale SEO optimalisatie voor een advocatenkantoor dat nu op de eerste pagina staat voor alle relevante zoekwoorden.',
      category: 'Local SEO',
      technologies: ['Google My Business', 'Local Citations', 'Review Management', 'Schema Markup'],
      features: ['Eerste Pagina Rankings', 'Lokale Zichtbaarheid', 'Review Score 4.8'],
      link: '#'
    },
    {
      key: 'seo3',
      title: 'Content Marketing Success',
      description: 'Content-gebaseerde SEO strategie voor een B2B bedrijf dat organisch verkeer verdubbelde in 6 maanden.',
      category: 'Content SEO',
      technologies: ['Content Strategy', 'Keyword Research', 'Technical SEO', 'Analytics'],
      features: ['100% Traffic Groei', '50+ Blog Posts', 'Lead Generation', 'Brand Authority'],
      link: '#'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const getCategoryIcon = (category) => {
    if (category.includes('E-commerce')) return <ChartLine className="w-5 h-5" weight="thin" />;
    if (category.includes('Local')) return <Target className="w-5 h-5" weight="thin" />;
    return <MagnifyingGlass className="w-5 h-5" weight="thin" />;
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Onze <span className="text-blue-600">SEO resultaten</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van e-commerce tot lokale bedrijven. Bekijk enkele van onze SEO successen 
            en laat je inspireren door de resultaten die we behalen.
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.key}
              variants={itemVariants}
              className="group"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                {/* Project Header */}
                <div className="p-8">
                  {/* Category Badge */}
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    {getCategoryIcon(item.category)}
                    <span className="ml-2">{item.category}</span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">SEO Strategieën:</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.technologies.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Results */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Behaalde resultaten:</h4>
                    <div className="space-y-2">
                      {item.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-3xl p-12 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Wil je ook zulke SEO resultaten?</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Laat ons jouw website optimaliseren voor zoekmachines. Van keyword onderzoek 
              tot technische optimalisatie - we zorgen voor meetbare resultaten.
            </p>
            <Link to="/start-your-project" className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <ChartLine weight="thin" className="w-6 h-6" />
                Start je SEO project
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" weight="thin" />
              </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SeoPortfolio;
